import { useMutation } from "@apollo/react-hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToasts } from "react-toast-notifications";
import { getGraphQLErrorMessage } from "@dhn/shared/utils";
import { openCreditNoteReport } from "../../reports/creditNoteReport";
import { POPUP_BLOCKED_MESSAGE, tryOpenReport } from "../../reports/openReport";
import { INVOICE_NOTE_CREDIT_MUTATION } from "./queries";

const INITIAL_STATE = {
  insurerName: "",
  insurerRnc: "",
  ncf: "",
  applyNcf: "",
  concept: "",
  value: "",
};

const ITBIS_RATE = 0.18;

const round2 = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

export default function useActions({
  isOpen,
  toggle,
  selectedBilling,
  monthlyClosingId,
  monthlyClosing,
  onSaved,
}) {
  const [state, setState] = useState(INITIAL_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const { addToast } = useToasts();
  const [saveCreditNote, { loading: loadingSaveCreditNote }] = useMutation(
    INVOICE_NOTE_CREDIT_MUTATION,
  );

  const onChange = useCallback((field, value) => {
    setState((s) => ({ ...s, [field]: value }));
  }, []);

  const valueNumber = round2(Number(state.value || 0));
  const availableAmount = round2(Number(
    selectedBilling?.availableValue ?? selectedBilling?.contractedValue ?? 0,
  ));
  // Igual que la API: el ITBIS aplica solo a la parte de servicios con ITBIS (hasITBIS).
  const taxableRatio = useMemo(() => {
    const coverage = Number(selectedBilling?.coverageValue || 0);
    const taxableCoverage = Number(selectedBilling?.taxableCoverageValue || 0);
    return coverage > 0 ? Math.min(taxableCoverage / coverage, 1) : 0;
  }, [selectedBilling?.coverageValue, selectedBilling?.taxableCoverageValue]);
  const itbisAmount = useMemo(
    () => round2(valueNumber * taxableRatio * ITBIS_RATE),
    [taxableRatio, valueNumber],
  );
  const total = useMemo(
    () => round2(valueNumber + itbisAmount),
    [itbisAmount, valueNumber],
  );
  const liveRemainingAmount = round2(Math.max(availableAmount - valueNumber, 0));
  const exceedsAvailableAmount = valueNumber > availableAmount;

  const issueCreditNote = useCallback(async () => {
    const logoDataUrl = sessionStorage.getItem("imageLogo") || undefined;

    if (!selectedBilling?.saleIds?.length) {
      addToast("Debe seleccionar una factura valida", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    if (!valueNumber || valueNumber <= 0) {
      addToast("Debe digitar un valor mayor a cero", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    if (availableAmount <= 0) {
      addToast(
        "Esta factura no tiene monto disponible para una nueva nota de credito",
        {
          appearance: "warning",
          autoDismiss: true,
        },
      );
      return;
    }

    if (exceedsAvailableAmount) {
      addToast(
        "El valor de la Nota de Crédito no puede exceder el balance de la Factura",
        {
          appearance: "error",
          autoDismiss: true,
        },
      );
      return;
    }

    if (!state.concept?.trim()) {
      addToast("Debe ingresar el concepto de la nota de credito", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    let mutationResult;

    try {
      mutationResult = await saveCreditNote({
        variables: {
          data: {
            monthlyClosingId:
              selectedBilling.monthlyClosingId || monthlyClosingId,
            arsId: selectedBilling.arsId,
            ncf: selectedBilling.ncf,
            saleIds: selectedBilling.saleIds,
            amount: round2(valueNumber),
            itbis: round2(itbisAmount),
            total: round2(total),
            concept: state.concept,
          },
        },
      });
    } catch (error) {
      addToast(getGraphQLErrorMessage(error, "No fue posible emitir la nota de credito"), {
        appearance: "error",
        autoDismiss: true,
      });
      return;
    }

    const creditNote = mutationResult?.data?.saveConsultoryCreditNote;

    if (!creditNote) {
      addToast("No fue posible generar la nota de credito", {
        appearance: "error",
        autoDismiss: true,
      });
      return;
    }

    const reportOpened = tryOpenReport(openCreditNoteReport, {
      date: creditNote?.date || new Date(),
      logoDataUrl,
      consultoryName: monthlyClosing?.pharmacyProvider?.name,
      consultoryAddress: monthlyClosing?.pharmacyProvider?.address,
      consultoryAddressLine1: monthlyClosing?.pharmacyProvider?.name,
      consultoryAddressLine2: monthlyClosing?.pharmacyProvider?.address,
      consultoryAddressLine3: monthlyClosing?.pharmacyProvider?.sector?.name || "",
      consultoryRnc: monthlyClosing?.pharmacyProvider?.nif,
      insurerName: creditNote?.insurerName,
      insurerRnc: creditNote?.insurerRnc,
      voucherName: creditNote?.voucherName,
      voucherDueDate: creditNote?.voucherDueDate,
      ncf: creditNote?.ncf,
      applyNcf: creditNote?.applyNcf,
      concept: creditNote?.concept,
      value: creditNote?.amount,
      itbis: creditNote?.itbis,
      total: creditNote?.total,
    });

    let refreshedBilling = null;

    if (onSaved) {
      try {
        const refreshed = await onSaved();
        const rows = refreshed?.data?.getConsultoryMonthlyClosedDetail || [];

        refreshedBilling =
          rows.find((item) => item?.id === selectedBilling?.id) ||
          rows.find(
            (item) =>
              item?.arsId === selectedBilling?.arsId &&
              `${item?.ncf || ""}` === `${selectedBilling?.ncf || ""}`,
          ) ||
          null;
      } catch (refreshError) {
        // Si la query origen esta en skip/no montada, Apollo puede lanzar en refetch.
      }
    }

    const updatedAvailable = Number(
      refreshedBilling?.availableValue ??
        refreshedBilling?.contractedValue ??
        availableAmount,
    );

    addToast(
      `Nota de credito emitida correctamente. Disponible actualizado: ${updatedAvailable.toFixed(
        2,
      )}`,
      {
        appearance: "success",
        autoDismiss: true,
      },
    );

    if (!reportOpened) {
      addToast(POPUP_BLOCKED_MESSAGE, { appearance: "warning", autoDismiss: true });
    }

    toggle();
  }, [
    addToast,
    itbisAmount,
    onSaved,
    saveCreditNote,
    selectedBilling,
    state.concept,
    toggle,
    total,
    valueNumber,
    availableAmount,
    exceedsAvailableAmount,
    monthlyClosingId,
    monthlyClosing,
  ]);

  // Evita emitir dos notas de credito mientras se guarda y se refresca el detalle.
  const handleShowReport = useCallback(async () => {
    if (submittingRef.current) return;

    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      await issueCreditNote();
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [issueCreditNote]);

  useEffect(() => {
    if (!isOpen || !selectedBilling) {
      setState(INITIAL_STATE);
      return;
    }

    setState({
      ...INITIAL_STATE,
      insurerName: selectedBilling?.insurerName || "",
      insurerRnc: selectedBilling?.insurerRnc || "",
      applyNcf: selectedBilling?.ncf || "",
      ncf: "Genera el Proximo",
    });
  }, [isOpen, selectedBilling]);

  return [
    {
      state,
      total,
      itbisAmount,
      loadingSaveCreditNote: loadingSaveCreditNote || isSubmitting,
      availableAmount,
      liveRemainingAmount,
      exceedsAvailableAmount,
    },
    {
      handleShowReport,
      onChange,
    },
  ];
}
